import React, { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import useEscape from "../lib/useEscape";
import { useNavigate } from "react-router-dom";
import api, { categoryLabel } from "../lib/api";

export default function SearchOverlay({ open, onClose }) {
  useEscape(open, onClose);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (!q) { setResults([]); return; }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const res = await api.get("/products", { params: { q, limit: 12 } });
        setResults(res.data.products || []);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(id);
  }, [q]);

  if (!open) return null;

  const go = (p) => {
    const productName = p.product || p.name || "";
    const slug = p.category || "automobiles";
    onClose();
    navigate(`/${slug}?q=${encodeURIComponent(productName)}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/60 flex items-start justify-center pt-20 px-4" onClick={onClose}>
      <div className="bg-card w-full max-w-2xl rounded-none border border-border shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="search-overlay">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
          <Search size={18} className="text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Smart search — Brand · Product · Tag · Category (e.g. 'royal enfield', 'classic350', 'apartment')"
            className="flex-1 outline-none text-base"
            data-testid="search-input"
          />
          <button onClick={onClose} className="p-1 hover:bg-foreground/10 rounded" data-testid="search-overlay-close"><X size={16} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {!q && (
            <div className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Popular searches</p>
              <div className="flex flex-wrap gap-2">
                {["Hyundai Creta", "Royal Enfield Classic 350", "Honda Activa", "Kia Seltos", "Sun Optima", "Gala Aria", "Samsung S26", "iPhone"].map((t) => (
                  <button key={t} onClick={() => setQ(t)} className="px-3 py-1.5 border border-border rounded-full text-xs hover:border-foreground/30" data-testid={`search-chip-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</button>
                ))}
              </div>
            </div>
          )}
          {q && loading && (
            <p className="text-center text-sm text-muted-foreground py-8">Searching…</p>
          )}
          {q && !loading && results.length === 0 && (
            <div className="text-center py-10 px-5">
              <p className="text-sm text-muted-foreground">No products match &ldquo;{q}&rdquo;.</p>
              <p className="text-xs text-muted-foreground mt-2">
                Try removing spaces or checking the spelling — e.g. <em>royalenfieldclassic350</em> still finds <strong>Royal Enfield Classic 350</strong>.
              </p>
            </div>
          )}
          <ul className="divide-y divide-border">
            {results.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => go(p)}
                  className="w-full px-5 py-3 flex items-center gap-3 hover:bg-foreground/10 text-left"
                  data-testid={`search-result-${p.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#FFBE06] flex items-center justify-center text-black text-base shrink-0">
                    <span aria-hidden>✊</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                      {p.brand} · {categoryLabel(p.category)}
                    </div>
                    <div className="font-semibold truncate">{p.product || p.name}</div>
                  </div>
                  <div className="text-xs bhega-pill bg-[#FFBE06]/15 border border-[#FFBE06]/40 text-foreground font-bold shrink-0">
                    {p.bhega_count ?? 0} Bhega
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
